import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from './SearchableSelect';
import { LocationPickerModal } from './LocationPickerModal';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';
import { logEvent } from '../lib/telemetry';
import { Plus, CreditCard as Edit2, Trash2, X, Save, Monitor, ChevronDown, ChevronUp, Grid3x3, MapPin, AlertTriangle } from 'lucide-react';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { WaterScheduleManager, WaterSchedule } from './WaterScheduleManager';
import { WaterRestrictionPolicyCard } from './WaterRestrictionPolicyCard';
import {
  findScheduleViolations,
  resolvePropertyRule,
  parseStreetNumber,
  type PropertyWaterPolicy,
  type Violation,
  type DayAbbr,
} from '../lib/waterRestrictions';
import { PhotoInput } from './PhotoInput';
import { apiClient } from '../lib/apiClient';
import { PHOTO_BUCKETS, deletePhotoByUrl, uploadPhotoFile } from '../lib/photoUpload';
import { MiniMapPreview } from './MiniMapPreview';
import { readGpsLocation } from '../lib/googleMaps';
import type { GPSLocation } from '../lib/googleMaps';

interface Zone {
  id: string;
  controller_id: string;
  zone_number: number;
  type: string;
  location: string;
  photo_url: string | null;
  gps_location: GPSLocation | null;
}

interface Manufacturer {
  id: string;
  name: string;
}

interface ZoneTypeOption {
  id: string;
  name: string;
  is_default: boolean;
}

interface Controller {
  id: string;
  job_site_id: string;
  controller_number: number;
  description: string;
  location: string;
  photo_url: string | null;
  manufacturer_id: string | null;
  model: string | null;
  water_start_times: WaterSchedule;
  water_restriction_override: { acknowledged?: boolean; acknowledged_at?: string } | null;
  gps_location: GPSLocation | null;
  notes: string | null;
  created_at: string;
  zones: Zone[];
  controller_manufacturers?: {
    name: string;
  };
}

interface JobSiteControllersProps {
  jobSiteId: string;
  jobSiteName: string;
  onDataChange?: () => void;
  /**
   * View-only mode (Manager property screen). Every add / edit / delete
   * affordance is removed from the DOM — not merely disabled — so the write
   * forms are unreachable. The server is the real gate; this keeps the screen
   * honest about what the viewer can do.
   */
  readOnly?: boolean;
}

export function JobSiteControllers({ jobSiteId, jobSiteName, onDataChange, readOnly = false }: JobSiteControllersProps) {
  const { t } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [zoneTypes, setZoneTypes] = useState<ZoneTypeOption[]>([]);
  const [defaultZoneType, setDefaultZoneType] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isSavingController, setIsSavingController] = useState(false);
  const [isSavingZone, setIsSavingZone] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedController, setExpandedController] = useState<string | null>(null);
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [addingZoneToController, setAddingZoneToController] = useState<string | null>(null);
  const [bulkAddingToController, setBulkAddingToController] = useState<string | null>(null);
  const [selectedZones, setSelectedZones] = useState<Set<string>>(new Set());
  const [bulkManageModal, setBulkManageModal] = useState<{ controllerId: string; zones: Zone[] } | null>(null);
  const [bulkDeleteConfirmModal, setBulkDeleteConfirmModal] = useState<{ zones: Zone[] } | null>(null);
  const [isDeletingZones, setIsDeletingZones] = useState(false);
  const [bulkUpdateFormData, setBulkUpdateFormData] = useState({
    type: '',
    location: '',
  });
  const [bulkZoneFormData, setBulkZoneFormData] = useState({
    start_number: '',
    end_number: '',
    type: '',
    location_pattern: '',
  });
  const [formData, setFormData] = useState({
    controller_number: '',
    description: '',
    location: '',
    manufacturer_id: '',
    model: '',
    water_start_times: { programs: [] } as WaterSchedule,
    gps_location: null as GPSLocation | null,
    notes: '',
    photo: null as File | null,
  });
  const [zoneFormData, setZoneFormData] = useState({
    zone_number: '',
    type: '',
    location: '',
    gps_location: null as GPSLocation | null,
    photo: null as File | null,
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [zonePhotoPreview, setZonePhotoPreview] = useState<string | null>(null);
  const [controllerGpsModalOpen, setControllerGpsModalOpen] = useState(false);
  const [zoneGpsModalOpen, setZoneGpsModalOpen] = useState(false);
  const [deleteControllerConfirm, setDeleteControllerConfirm] = useState<{ id: string; description: string } | null>(null);
  const [deleteZoneConfirm, setDeleteZoneConfirm] = useState<{ id: string; number: number } | null>(null);
  const [bulkAddResultModal, setBulkAddResultModal] = useState<{ success: boolean; message: string } | null>(null);
  // Water restriction policy for this property (loaded by the policy card) + override prompt state.
  const [waterPolicy, setWaterPolicy] = useState<PropertyWaterPolicy | null>(null);
  const [overrideViolations, setOverrideViolations] = useState<Violation[] | null>(null);
  // Property's saved location — fallback map center for the GPS picker when a
  // device has no location yet and live GPS is blocked/unavailable in the field.
  const [propertyLocation, setPropertyLocation] = useState<GPSLocation | null>(null);

  useEffect(() => {
    loadControllers();
    loadManufacturers();
    loadZoneTypes();
    loadWaterPolicy();
    loadPropertyLocation();
  }, [jobSiteId]);

  const loadPropertyLocation = async () => {
    try {
      const js = await apiClient.get<{ gps_location: unknown }>(`/api/job-sites/${jobSiteId}`);
      setPropertyLocation(readGpsLocation(js?.gps_location));
    } catch {
      setPropertyLocation(null);
    }
  };

  const loadWaterPolicy = async () => {
    try {
      setWaterPolicy(await apiClient.get<PropertyWaterPolicy>(`/api/job-sites/${jobSiteId}/water-restriction`));
    } catch (err) {
      console.error('Error loading water restriction policy:', err);
    }
  };

  const loadManufacturers = async () => {
    try {
      const all = await apiClient.get<Manufacturer[]>('/api/controller-manufacturers');
      const sorted = [...all].sort((a, b) => a.name.localeCompare(b.name));
      setManufacturers(sorted);
    } catch (err) {
      console.error('Error loading manufacturers:', err);
    }
  };

  const loadZoneTypes = async () => {
    try {
      const data = await apiClient.get<ZoneTypeOption[]>('/api/zone-types');
      const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
      setZoneTypes(sorted);

      const defaultType = sorted.find((type) => type.is_default);
      if (defaultType) {
        setDefaultZoneType(defaultType.name);
      }
    } catch (err) {
      console.error('Error loading zone types:', err);
    }
  };

  const loadControllers = async () => {
    try {
      const [allControllers, allZones, allManufacturers] = await Promise.all([
        apiClient.get<Controller[]>('/api/controllers'),
        apiClient.get<Zone[]>('/api/zones'),
        apiClient.get<Manufacturer[]>('/api/controller-manufacturers'),
      ]);

      const manufacturerMap = new Map(allManufacturers.map((m) => [m.id, m.name]));
      const zonesByController = new Map<string, Zone[]>();
      for (const z of allZones) {
        const list = zonesByController.get(z.controller_id) ?? [];
        list.push(z);
        zonesByController.set(z.controller_id, list);
      }

      const filtered = allControllers
        .filter((c) => c.job_site_id === jobSiteId)
        .sort((a, b) => a.controller_number - b.controller_number)
        .map((c) => ({
          ...c,
          water_start_times: c.water_start_times || { programs: [] },
          controller_manufacturers: c.manufacturer_id
            ? { name: manufacturerMap.get(c.manufacturer_id) ?? '' }
            : undefined,
          zones: (zonesByController.get(c.id) ?? [])
            .slice()
            .sort((a, b) => a.zone_number - b.zone_number),
        }));

      setControllers(filtered);
    } catch (err) {
      console.error('Error loading controllers:', err);
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (controllerId: string, file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      return await uploadPhotoFile(file, PHOTO_BUCKETS.CONTROLLER, `controllers/${controllerId}/photo.${fileExt}`);
    } catch (err) {
      console.error('Error uploading photo:', err);
      return null;
    }
  };

  const uploadZonePhoto = async (zoneId: string, file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      return await uploadPhotoFile(file, PHOTO_BUCKETS.ZONE, `zones/${zoneId}/photo.${fileExt}`);
    } catch (err) {
      console.error('Error uploading zone photo:', err);
      return null;
    }
  };

  const deleteControllerPhoto = async (controllerId: string) => {
    try {
      const oldUrl = controllers.find((c) => c.id === controllerId)?.photo_url ?? null;
      await apiClient.put(`/api/controllers/${controllerId}`, { photo_url: null });
      await deletePhotoByUrl(oldUrl);
      setPhotoPreview(null);
      setFormData({ ...formData, photo: null });
      await loadControllers();
    } catch (err) {
      console.error('Error deleting controller photo:', err);
      notify(tCommon('status.errorTitle'), t('controllers.errorDeletePhoto'), 'danger');
    }
  };

  const deleteZonePhoto = async (zoneId: string) => {
    try {
      const oldUrl = controllers
        .flatMap((c) => c.zones ?? [])
        .find((z) => z.id === zoneId)?.photo_url ?? null;
      await apiClient.put(`/api/zones/${zoneId}`, { photo_url: null });
      await deletePhotoByUrl(oldUrl);
      setZonePhotoPreview(null);
      setZoneFormData({ ...zoneFormData, photo: null });
      await loadControllers();
    } catch (err) {
      console.error('Error deleting zone photo:', err);
      notify(tCommon('status.errorTitle'), t('zones.errorDeletePhoto'), 'danger');
    }
  };

  const handleSaveController = async () => {
    if (readOnly) return;
    if (isSavingController) return;
    if (!formData.controller_number.trim() || !formData.description.trim() || !formData.location.trim()) {
      notify(tCommon('status.noticeTitle'), t('controllers.errorFillFields'), 'warning');
      return;
    }

    const controllerNum = parseInt(formData.controller_number);
    if (isNaN(controllerNum) || controllerNum < 1) {
      notify(tCommon('status.noticeTitle'), t('controllers.errorControllerNumPositive'), 'warning');
      return;
    }

    // Client-side duplicate guard: controller numbers are unique per property
    // (DB: UNIQUE(job_site_id, controller_number)). Catch a collision here with a
    // clear message instead of letting the POST hard-fail with a generic 409.
    const dupController = controllers.some(
      (c) => c.controller_number === controllerNum && c.id !== editingId,
    );
    if (dupController) {
      notify(tCommon('status.noticeTitle'), t('controllers.errorDuplicateNumber', { num: controllerNum }), 'warning');
      return;
    }

    // Gate on the water restriction policy: a violating schedule must be
    // explicitly overridden before it can be applied.
    const violations = violationsFor(formData.water_start_times);
    if (violations.length > 0) {
      setOverrideViolations(violations);
      return;
    }
    await persistController(controllerNum, null);
  };

  /**
   * Persists the controller. `overrideAck` is non-null only when the tech chose
   * to apply a schedule that violates the policy; it is stored so the schedule
   * shows "overridden" instead of a standing violation warning. A compliant save
   * passes null, clearing any stale override.
   */
  const persistController = async (controllerNum: number, overrideAck: Record<string, unknown> | null) => {
    setIsSavingController(true);
    try {
      if (editingId) {
        let photoUrl = controllers.find((c) => c.id === editingId)?.photo_url;

        if (formData.photo) {
          const uploadedUrl = await uploadPhoto(editingId, formData.photo);
          if (uploadedUrl) photoUrl = uploadedUrl;
        }

        await apiClient.put(`/api/controllers/${editingId}`, {
          controller_number: controllerNum,
          description: formData.description.trim(),
          location: formData.location.trim(),
          manufacturer_id: formData.manufacturer_id || null,
          model: formData.model.trim() || null,
          water_start_times: formData.water_start_times,
          water_restriction_override: overrideAck,
          gps_location: formData.gps_location,
          notes: formData.notes.trim() || null,
          photo_url: photoUrl,
        });

        void logEvent('inspection_device_updated', `Updated controller #${controllerNum}`, {
          metadata: { device_type: 'controller', device_id: editingId, controller_number: controllerNum, label: formData.description.trim(), job_site_id: jobSiteId },
        });
        const editedCtrl = controllers.find((c) => c.id === editingId);
        logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.CONTROLLER,
          entityId: editingId,
          entityLabel: `Controller ${controllerNum} - ${formData.description.trim()}`,
          parentEntityType: AUDIT_ENTITIES.PROPERTY,
          parentEntityId: jobSiteId,
          parentEntityLabel: jobSiteName,
          details: computeChanges(
            { controller_number: editedCtrl?.controller_number, description: editedCtrl?.description, location: editedCtrl?.location, manufacturer_id: editedCtrl?.manufacturer_id, model: editedCtrl?.model, photo_url: editedCtrl?.photo_url },
            { controller_number: controllerNum, description: formData.description.trim(), location: formData.location.trim(), manufacturer_id: formData.manufacturer_id || null, model: formData.model.trim() || null, photo_url: photoUrl },
          ),
          pageContext: 'controllers',
        });
      } else {
        const newController = await apiClient.post<Controller>('/api/controllers', {
          job_site_id: jobSiteId,
          controller_number: controllerNum,
          description: formData.description.trim(),
          location: formData.location.trim(),
          manufacturer_id: formData.manufacturer_id || null,
          model: formData.model.trim() || null,
          water_start_times: formData.water_start_times,
          water_restriction_override: overrideAck,
          gps_location: formData.gps_location,
          notes: formData.notes.trim() || null,
        });

        if (formData.photo && newController) {
          const photoUrl = await uploadPhoto(newController.id, formData.photo);
          if (photoUrl) {
            await apiClient.put(`/api/controllers/${newController.id}`, { photo_url: photoUrl });
          }
        }
        void logEvent('inspection_device_added', `Added controller #${controllerNum}`, {
          metadata: { device_type: 'controller', device_id: newController.id, controller_number: controllerNum, label: formData.description.trim(), job_site_id: jobSiteId },
        });
        logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.CONTROLLER,
          entityId: newController.id,
          entityLabel: `Controller ${controllerNum} - ${formData.description.trim()}`,
          parentEntityType: AUDIT_ENTITIES.PROPERTY,
          parentEntityId: jobSiteId,
          parentEntityLabel: jobSiteName,
          details: { controller_number: controllerNum, description: formData.description.trim(), location: formData.location.trim(), manufacturer_id: formData.manufacturer_id || null, model: formData.model.trim() || null },
          pageContext: 'controllers',
        });
      }

      resetControllerForm();
      await loadControllers();
      onDataChange?.();
    } catch (err) {
      console.error('Error saving controller:', err);
      notify(tCommon('status.errorTitle'), t('controllers.errorSaveController'), 'danger');
    } finally {
      setIsSavingController(false);
    }
  };

  const handleSaveZone = async (controllerId: string) => {
    if (readOnly) return;
    if (isSavingZone) return;
    if (!zoneFormData.zone_number.trim() || !zoneFormData.type.trim() || !zoneFormData.location.trim()) {
      notify(tCommon('status.noticeTitle'), t('zones.errorFillFields'), 'warning');
      return;
    }

    const zoneNum = parseInt(zoneFormData.zone_number);
    if (isNaN(zoneNum) || zoneNum < 1) {
      notify(tCommon('status.noticeTitle'), t('zones.errorZoneNumPositive'), 'warning');
      return;
    }

    // Client-side duplicate guard: zone numbers are unique per controller
    // (DB: UNIQUE(controller_id, zone_number)). Catch a collision here instead of
    // a generic 409.
    const dupZone = (controllers.find((c) => c.id === controllerId)?.zones ?? []).some(
      (z) => z.zone_number === zoneNum && z.id !== editingZoneId,
    );
    if (dupZone) {
      notify(tCommon('status.noticeTitle'), t('zones.errorDuplicateNumber', { num: zoneNum }), 'warning');
      return;
    }

    setIsSavingZone(true);
    try {
      if (editingZoneId) {
        const controller = controllers.find((c) => c.id === controllerId);
        const zone = controller?.zones.find((z) => z.id === editingZoneId);
        let photoUrl = zone?.photo_url;

        if (zoneFormData.photo) {
          const uploadedUrl = await uploadZonePhoto(editingZoneId, zoneFormData.photo);
          if (uploadedUrl) photoUrl = uploadedUrl;
        }

        await apiClient.put(`/api/zones/${editingZoneId}`, {
          zone_number: zoneNum,
          type: zoneFormData.type,
          location: zoneFormData.location.trim(),
          gps_location: zoneFormData.gps_location,
          photo_url: photoUrl,
        });

        void logEvent('inspection_device_updated', `Updated zone #${zoneNum}`, {
          metadata: { device_type: 'zone', device_id: editingZoneId, zone_number: zoneNum, controller_id: controllerId, type: zoneFormData.type, job_site_id: jobSiteId },
        });
        logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.ZONE,
          entityId: editingZoneId,
          entityLabel: `Zone ${zoneNum}`,
          parentEntityType: AUDIT_ENTITIES.CONTROLLER,
          parentEntityId: controllerId,
          details: computeChanges(
            { zone_number: zone?.zone_number, type: zone?.type, location: zone?.location, photo_url: zone?.photo_url },
            { zone_number: zoneNum, type: zoneFormData.type, location: zoneFormData.location.trim(), photo_url: photoUrl },
          ),
          pageContext: 'zones',
        });
      } else {
        const newZone = await apiClient.post<Zone>('/api/zones', {
          controller_id: controllerId,
          zone_number: zoneNum,
          type: zoneFormData.type,
          location: zoneFormData.location.trim(),
          gps_location: zoneFormData.gps_location,
        });

        if (zoneFormData.photo && newZone) {
          const photoUrl = await uploadZonePhoto(newZone.id, zoneFormData.photo);
          if (photoUrl) {
            await apiClient.put(`/api/zones/${newZone.id}`, { photo_url: photoUrl });
          }
        }
        void logEvent('inspection_device_added', `Added zone #${zoneNum}`, {
          metadata: { device_type: 'zone', device_id: newZone.id, zone_number: zoneNum, controller_id: controllerId, type: zoneFormData.type, job_site_id: jobSiteId },
        });
        logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.ZONE,
          entityId: newZone.id,
          entityLabel: `Zone ${zoneNum}`,
          parentEntityType: AUDIT_ENTITIES.CONTROLLER,
          parentEntityId: controllerId,
          details: { zone_number: zoneNum, type: zoneFormData.type, location: zoneFormData.location.trim() },
          pageContext: 'zones',
        });
      }

      resetZoneForm();
      await loadControllers();
      onDataChange?.();
    } catch (err) {
      console.error('Error saving zone:', err);
      notify(tCommon('status.errorTitle'), t('zones.errorSaveZone'), 'danger');
    } finally {
      setIsSavingZone(false);
    }
  };

  const handleBulkAddZones = async (controllerId: string) => {
    if (readOnly) return;
    if (!bulkZoneFormData.start_number || !bulkZoneFormData.end_number || !bulkZoneFormData.type) {
      notify(tCommon('status.noticeTitle'), t('zones.errorBulkRequiredFields'), 'warning');
      return;
    }

    const startNum = parseInt(bulkZoneFormData.start_number);
    const endNum = parseInt(bulkZoneFormData.end_number);

    if (isNaN(startNum) || isNaN(endNum) || startNum < 1 || endNum < startNum) {
      notify(tCommon('status.noticeTitle'), t('zones.errorBulkInvalidRange'), 'warning');
      return;
    }

    const zoneCount = endNum - startNum + 1;
    if (zoneCount > 100) {
      notify(tCommon('status.noticeTitle'), t('zones.errorBulkMax100'), 'warning');
      return;
    }

    // Don't let the range overlap zone numbers that already exist on this
    // controller — those would each hard-fail with a 409.
    const existingNums = new Set(
      (controllers.find((c) => c.id === controllerId)?.zones ?? []).map((z) => z.zone_number),
    );
    const collisions: number[] = [];
    for (let i = startNum; i <= endNum; i++) if (existingNums.has(i)) collisions.push(i);
    if (collisions.length > 0) {
      notify(tCommon('status.noticeTitle'), t('zones.errorBulkDuplicate', { nums: collisions.slice(0, 12).join(', ') }), 'warning');
      return;
    }

    try {
      for (let i = startNum; i <= endNum; i++) {
        const location = bulkZoneFormData.location_pattern
          ? bulkZoneFormData.location_pattern.replace('{number}', i.toString())
          : `Zone ${i}`;
        await apiClient.post<Zone>('/api/zones', {
          controller_id: controllerId,
          zone_number: i,
          type: bulkZoneFormData.type,
          location,
        });
      }

      void logEvent('inspection_device_added', `Bulk added ${zoneCount} zones (${startNum}-${endNum})`, {
        metadata: {
          device_type: 'zone',
          mode: 'bulk',
          controller_id: controllerId,
          zone_count: zoneCount,
          start_number: startNum,
          end_number: endNum,
          type: bulkZoneFormData.type,
          job_site_id: jobSiteId,
        },
      });
      logAudit({
        action: AUDIT_ACTIONS.CREATE,
        entityType: AUDIT_ENTITIES.ZONE,
        entityLabel: `${zoneCount} zones (${startNum}-${endNum})`,
        parentEntityType: AUDIT_ENTITIES.CONTROLLER,
        parentEntityId: controllerId,
        details: { zoneCount, startNum, endNum },
        pageContext: 'zones',
      });
      resetBulkZoneForm();
      await loadControllers();
      onDataChange?.();
      setBulkAddResultModal({
        success: true,
        message: t('zones.bulkAddSuccess', { count: zoneCount }),
      });
    } catch (err) {
      console.error('Error bulk adding zones:', err);
      setBulkAddResultModal({
        success: false,
        message: t('zones.errorBulkAdd'),
      });
    }
  };

  const toggleZoneSelection = (zoneId: string) => {
    const newSelected = new Set(selectedZones);
    if (newSelected.has(zoneId)) {
      newSelected.delete(zoneId);
    } else {
      newSelected.add(zoneId);
    }
    setSelectedZones(newSelected);
  };

  const toggleAllZonesInController = (controllerId: string) => {
    const controller = controllers.find((c) => c.id === controllerId);
    if (!controller) return;

    const controllerZoneIds = controller.zones.map((z) => z.id);
    const allSelected = controllerZoneIds.every((id) => selectedZones.has(id));

    const newSelected = new Set(selectedZones);
    if (allSelected) {
      controllerZoneIds.forEach((id) => newSelected.delete(id));
    } else {
      controllerZoneIds.forEach((id) => newSelected.add(id));
    }
    setSelectedZones(newSelected);
  };

  const openBulkManageModal = (controllerId: string) => {
    const controller = controllers.find((c) => c.id === controllerId);
    if (!controller) return;

    const selectedZonesInController = controller.zones.filter((z) => selectedZones.has(z.id));
    setBulkManageModal({ controllerId, zones: selectedZonesInController });
    setBulkUpdateFormData({ type: '', location: '' });
  };

  const handleBulkUpdateZones = async () => {
    if (readOnly) return;
    if (!bulkManageModal) return;

    if (!bulkUpdateFormData.type && !bulkUpdateFormData.location) {
      notify(tCommon('status.noticeTitle'), t('zones.errorBulkUpdateEmpty'), 'warning');
      return;
    }

    try {
      const updates: Record<string, string> = {};
      if (bulkUpdateFormData.type) updates.type = bulkUpdateFormData.type;
      if (bulkUpdateFormData.location) updates.location = bulkUpdateFormData.location;

      const zoneIds = bulkManageModal.zones.map((z) => z.id);
      for (const id of zoneIds) {
        await apiClient.put(`/api/zones/${id}`, updates);
      }

      void logEvent('inspection_device_updated', `Bulk updated ${zoneIds.length} zones`, {
        metadata: {
          device_type: 'zone',
          mode: 'bulk',
          controller_id: bulkManageModal.controllerId,
          zone_ids: zoneIds,
          zone_count: zoneIds.length,
          updates,
          job_site_id: jobSiteId,
        },
      });
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.ZONE,
        entityLabel: `${zoneIds.length} zones`,
        details: { updates, count: zoneIds.length },
        pageContext: 'zones',
      });
      setBulkManageModal(null);
      setSelectedZones(new Set());
      await loadControllers();
      onDataChange?.();
      setBulkAddResultModal({
        success: true,
        message: t('zones.bulkUpdateSuccess', { count: zoneIds.length }),
      });
    } catch (err) {
      console.error('Error bulk updating zones:', err);
      setBulkAddResultModal({
        success: false,
        message: t('zones.errorBulkUpdate'),
      });
    }
  };

  const handleBulkDeleteSelectedZonesRequest = () => {
    if (!bulkManageModal) return;
    setBulkDeleteConfirmModal({ zones: bulkManageModal.zones });
  };

  const handleBulkDeleteSelectedZones = async () => {
    if (readOnly) return;
    if (!bulkDeleteConfirmModal) return;

    setIsDeletingZones(true);

    try {
      const zoneIds = bulkDeleteConfirmModal.zones.map((z) => z.id);
      for (const id of zoneIds) {
        await apiClient.del(`/api/zones/${id}`);
      }

      void logEvent('inspection_device_deleted', `Bulk deleted ${zoneIds.length} zones`, {
        metadata: {
          device_type: 'zone',
          mode: 'bulk',
          zone_ids: zoneIds,
          zone_count: zoneIds.length,
          job_site_id: jobSiteId,
        },
      });
      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.ZONE,
        entityLabel: `${zoneIds.length} zones`,
        details: { count: zoneIds.length },
        pageContext: 'zones',
      });

      setBulkDeleteConfirmModal(null);
      setBulkManageModal(null);
      setSelectedZones(new Set());
      await loadControllers();
      onDataChange?.();
      setBulkAddResultModal({
        success: true,
        message: t('zones.bulkDeleteSuccess', { count: zoneIds.length }),
      });
    } catch (err) {
      console.error('Error bulk deleting zones:', err);
      setBulkDeleteConfirmModal(null);
      setBulkAddResultModal({
        success: false,
        message: t('zones.errorBulkDelete'),
      });
    } finally {
      setIsDeletingZones(false);
    }
  };

  const handleDeleteController = async () => {
    if (readOnly) return;
    if (!deleteControllerConfirm) return;

    try {
      await apiClient.del(`/api/controllers/${deleteControllerConfirm.id}`);

      void logEvent('inspection_device_deleted', `Deleted controller ${deleteControllerConfirm.description}`, {
        metadata: { device_type: 'controller', device_id: deleteControllerConfirm.id, label: deleteControllerConfirm.description, job_site_id: jobSiteId },
      });
      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.CONTROLLER,
        entityId: deleteControllerConfirm.id,
        entityLabel: deleteControllerConfirm.description,
        parentEntityType: AUDIT_ENTITIES.PROPERTY,
        parentEntityId: jobSiteId,
        parentEntityLabel: jobSiteName,
        pageContext: 'controllers',
      });

      setDeleteControllerConfirm(null);
      await loadControllers();
      onDataChange?.();
    } catch (err) {
      console.error('Error deleting controller:', err);
      notify(tCommon('status.errorTitle'), t('controllers.errorDeleteController'), 'danger');
    }
  };

  const handleDeleteZone = async () => {
    if (readOnly) return;
    if (!deleteZoneConfirm) return;

    try {
      await apiClient.del(`/api/zones/${deleteZoneConfirm.id}`);

      void logEvent('inspection_device_deleted', `Deleted zone #${deleteZoneConfirm.number}`, {
        metadata: { device_type: 'zone', device_id: deleteZoneConfirm.id, zone_number: deleteZoneConfirm.number, job_site_id: jobSiteId },
      });
      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.ZONE,
        entityId: deleteZoneConfirm.id,
        entityLabel: `Zone ${deleteZoneConfirm.number}`,
        pageContext: 'zones',
      });

      setDeleteZoneConfirm(null);
      await loadControllers();
      onDataChange?.();
    } catch (err) {
      console.error('Error deleting zone:', err);
      notify(tCommon('status.errorTitle'), t('zones.errorDeleteZone'), 'danger');
    }
  };

  const startEditController = (controller: Controller) => {
    setEditingId(controller.id);
    setFormData({
      controller_number: controller.controller_number.toString(),
      description: controller.description ?? '',
      location: controller.location ?? '',
      manufacturer_id: controller.manufacturer_id || '',
      model: controller.model || '',
      water_start_times: controller.water_start_times || { programs: [] },
      gps_location: controller.gps_location || null,
      notes: controller.notes || '',
      photo: null,
    });
    setPhotoPreview(controller.photo_url);
    setIsAdding(false);
  };

  const startEditZone = (zone: Zone) => {
    setEditingZoneId(zone.id);
    setZoneFormData({
      zone_number: zone.zone_number.toString(),
      type: zone.type,
      location: zone.location ?? '',
      gps_location: zone.gps_location || null,
      photo: null,
    });
    setZonePhotoPreview(zone.photo_url);
    setAddingZoneToController(null);
  };

  // Next available controller number for this property (max + 1), so the Add
  // form defaults to a number that won't collide.
  const nextControllerNumber = () =>
    controllers.reduce((m, c) => Math.max(m, c.controller_number), 0) + 1;

  const startAddController = () => {
    setEditingId(null);
    setFormData({
      controller_number: String(nextControllerNumber()),
      description: '',
      location: '',
      manufacturer_id: '',
      model: '',
      water_start_times: { programs: [] },
      gps_location: null,
      notes: '',
      photo: null,
    });
    setPhotoPreview(null);
    setIsAdding(true);
  };

  const resetControllerForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      controller_number: '',
      description: '',
      location: '',
      manufacturer_id: '',
      model: '',
      water_start_times: { programs: [] },
      gps_location: null,
      notes: '',
      photo: null,
    });
    setPhotoPreview(null);
  };

  const resetZoneForm = () => {
    setAddingZoneToController(null);
    setEditingZoneId(null);
    setZoneFormData({ zone_number: '', type: defaultZoneType, location: '', gps_location: null, photo: null });
    setZonePhotoPreview(null);
  };

  // Next available zone number for a controller (max + 1).
  const nextZoneNumber = (controllerId: string) =>
    (controllers.find((c) => c.id === controllerId)?.zones ?? []).reduce(
      (m, z) => Math.max(m, z.zone_number), 0,
    ) + 1;

  const startAddZone = (controllerId: string) => {
    setAddingZoneToController(controllerId);
    setEditingZoneId(null);
    setZoneFormData({ zone_number: String(nextZoneNumber(controllerId)), type: defaultZoneType, location: '', gps_location: null, photo: null });
    setZonePhotoPreview(null);
  };

  const captureZoneGPSLocation = () => setZoneGpsModalOpen(true);

  const resetBulkZoneForm = () => {
    setBulkAddingToController(null);
    setBulkZoneFormData({ start_number: '', end_number: '', type: defaultZoneType, location_pattern: '' });
  };

  const startBulkAddZones = (controllerId: string) => {
    setBulkAddingToController(controllerId);
    setAddingZoneToController(null);
    setEditingZoneId(null);
    setBulkZoneFormData({ start_number: String(nextZoneNumber(controllerId)), end_number: '', type: defaultZoneType, location_pattern: '' });
  };

  const toggleController = (controllerId: string) => {
    setExpandedController(expandedController === controllerId ? null : controllerId);
  };

  const handleWaterScheduleChange = (schedule: WaterSchedule) => {
    setFormData((prev) => ({
      ...prev,
      water_start_times: schedule,
    }));
  };

  // ----- Water restriction violation checking -----
  const dayLabel = (d: DayAbbr) => t(`schedule.day${d}`);

  const violationsFor = (schedule: WaterSchedule): Violation[] => {
    const rule = resolvePropertyRule(waterPolicy);
    if (!rule) return [];
    return findScheduleViolations(schedule?.programs, rule, parseStreetNumber(waterPolicy?.address));
  };

  const formatViolation = (v: Violation): string => {
    const city = waterPolicy?.city ?? tCommon('waterRestrictions.theCity', 'the city');
    if (v.kind === 'day') {
      return tCommon('waterRestrictions.violationDay', {
        program: v.program,
        days: (v.days ?? []).map(dayLabel).join(', '),
        allowed: (v.allowed ?? []).map(dayLabel).join(', '),
        city,
        defaultValue: `Program ${v.program} waters on ${(v.days ?? []).map(dayLabel).join(', ')}, which ${city} does not allow (allowed: ${(v.allowed ?? []).map(dayLabel).join(', ')}).`,
      });
    }
    if (v.kind === 'time') {
      return tCommon('waterRestrictions.violationTime', {
        program: v.program,
        window: v.window,
        defaultValue: `Program ${v.program} runs during the no-watering window (${v.window}).`,
      });
    }
    return tCommon('waterRestrictions.violationMaxDays', {
      count: v.count,
      max: v.max,
      city,
      defaultValue: `Schedule waters ${v.count} days/week; ${city} allows at most ${v.max}.`,
    });
  };

  const captureGPSLocation = () => setControllerGpsModalOpen(true);

  if (loading) {
    return <div className="text-theme-secondary">{t('controllers.loading')}</div>;
  }

  // unused state warning suppression
  void manufacturers;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-theme-primary">{t('controllers.heading', { name: jobSiteName })}</h3>
          <p className="text-sm text-theme-secondary">{t('controllers.subheading')}</p>
        </div>
        {!readOnly && !isAdding && !editingId && (
          <button
            onClick={startAddController}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            {t('controllers.addController')}
          </button>
        )}
      </div>

      {!readOnly && (isAdding || editingId) && (
        <div className="bg-subtle border border-line rounded-lg p-4">
          <h4 className="font-semibold text-theme-primary mb-4">
            {editingId ? t('controllers.editTitle') : t('controllers.newTitle')}
          </h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('controllers.manufacturer')}
                </label>
                <SearchableSelect
                  value={formData.manufacturer_id}
                  onChange={(v) => setFormData({ ...formData, manufacturer_id: v })}
                  options={manufacturers.map((mfr) => ({ value: mfr.id, label: mfr.name }))}
                  placeholder={t('controllers.selectManufacturer')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('controllers.model')}
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder={t('controllers.modelPlaceholder')}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('controllers.controllerNumberRequired')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.controller_number}
                  onChange={(e) => setFormData({ ...formData, controller_number: e.target.value })}
                  placeholder={t('controllers.controllerNumberPlaceholder')}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('controllers.descriptionRequired')}
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('controllers.descriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('controllers.locationRequired')}
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder={t('controllers.locationPlaceholder')}
                  className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <PhotoInput
                  label={t('controllers.photoLabel')}
                  preview={photoPreview}
                  placeholderIcon={<Monitor size={24} className="text-theme-secondary" />}
                  onFileChange={(file, preview) => {
                    setFormData((fd) => ({ ...fd, photo: file }));
                    setPhotoPreview(preview);
                  }}
                  onDelete={editingId
                    ? () => deleteControllerPhoto(editingId)
                    : () => { setPhotoPreview(null); setFormData((fd) => ({ ...fd, photo: null })); }}
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-theme-primary mb-2">
                    {t('gps.coordinates')}
                  </label>
                  {formData.gps_location ? (
                    <div className="space-y-2">
                      <div className="text-sm text-theme-primary">
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-green-600" />
                          <span>{t('gps.lat')} {formData.gps_location.latitude.toFixed(6)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <MapPin size={16} className="text-green-600" />
                          <span>{t('gps.lng')} {formData.gps_location.longitude.toFixed(6)}</span>
                        </div>
                      </div>
                      <button
                        onClick={captureGPSLocation}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <MapPin size={16} />
                        {t('gps.updateLocation')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={captureGPSLocation}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <MapPin size={16} />
                      {t('gps.setLocation')}
                    </button>
                  )}
                </div>
                {formData.gps_location && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-theme-primary mb-2">
                      {t('gps.locationMap')}
                    </label>
                    <MiniMapPreview
                      latitude={formData.gps_location.latitude}
                      longitude={formData.gps_location.longitude}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 border-t border-line pt-4 space-y-3">
            <WaterRestrictionPolicyCard jobSiteId={jobSiteId} onLoaded={setWaterPolicy} />
            {(() => {
              const liveViolations = violationsFor(formData.water_start_times);
              if (liveViolations.length === 0) return null;
              return (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-500/30 dark:bg-red-500/10">
                  <div className="flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-300">
                    <Monitor size={16} /> {tCommon('waterRestrictions.violationTitle', 'This schedule violates the water restriction policy')}
                  </div>
                  <ul className="mt-1 list-disc pl-6 text-sm text-red-700 dark:text-red-300">
                    {liveViolations.map((v, i) => (
                      <li key={i}>{formatViolation(v)}</li>
                    ))}
                  </ul>
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{tCommon('waterRestrictions.violationHint', 'You will be asked to override the policy when you save.')}</p>
                </div>
              );
            })()}
            <WaterScheduleManager
              schedule={formData.water_start_times}
              onChange={handleWaterScheduleChange}
            />
          </div>

          <div className="mt-4 border-t border-line pt-4">
            <label className="block text-sm font-medium text-theme-primary mb-2">
              {t('controllers.notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full p-3 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder={t('controllers.notesPlaceholder')}
            />
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSaveController}
              disabled={isSavingController}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {tCommon('actions.save')}
            </button>
            <button
              onClick={resetControllerForm}
              disabled={isSavingController}
              className="flex items-center gap-2 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={18} />
              {tCommon('actions.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {controllers.map((controller) => (
          <div
            key={controller.id}
            className="bg-surface border border-line rounded-lg overflow-hidden"
          >
            <div className="p-4 flex items-center justify-between hover:bg-subtle">
              <div className="flex items-center gap-4 flex-1">
                <button
                  onClick={() => toggleController(controller.id)}
                  className="p-1 hover:bg-subtle rounded transition-colors"
                >
                  {expandedController === controller.id ? (
                    <ChevronUp size={20} className="text-theme-secondary" />
                  ) : (
                    <ChevronDown size={20} className="text-theme-secondary" />
                  )}
                </button>
                {controller.photo_url ? (
                  <img
                    src={controller.photo_url}
                    alt={controller.description}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 bg-subtle rounded-lg flex items-center justify-center">
                    <Monitor size={24} className="text-theme-secondary" />
                  </div>
                )}
                <div>
                  {(controller.controller_manufacturers?.name || controller.model) && (
                    <div className="flex items-center gap-2 mb-1">
                      {controller.controller_manufacturers?.name && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 rounded-full font-medium">
                          {controller.controller_manufacturers.name}
                        </span>
                      )}
                      {controller.model && (
                        <span className="text-xs text-theme-secondary">
                          {controller.model}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-theme-primary">Controller {controller.controller_number}</h4>
                    <span className="text-sm text-theme-secondary">- {controller.description}</span>
                  </div>
                  <p className="text-sm text-theme-secondary">{controller.location}</p>
                  <p className="text-xs text-theme-secondary mt-1">{controller.zones.length} zones</p>
                  {(() => {
                    const v = violationsFor(controller.water_start_times);
                    if (v.length === 0) return null;
                    const overridden = controller.water_restriction_override?.acknowledged;
                    return (
                      <span
                        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          overridden ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300' : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300'
                        }`}
                        title={v.map(formatViolation).join('\n')}
                      >
                        <AlertTriangle size={12} />
                        {overridden
                          ? tCommon('waterRestrictions.overriddenBadge', 'Policy overridden')
                          : tCommon('waterRestrictions.violatesBadge', 'Violates watering policy')}
                      </span>
                    );
                  })()}
                </div>
              </div>
              {!readOnly && (
                <div className="flex gap-2">
                  <button
                    onClick={() => startEditController(controller)}
                    className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                    title={t('controllers.editTitle')}
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => setDeleteControllerConfirm({ id: controller.id, description: controller.description })}
                    className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                    title={t('controllers.deleteTitle')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            </div>

            {expandedController === controller.id && (
              <div className="border-t border-line bg-subtle p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-semibold text-theme-primary">{t('zones.heading')}</h5>
                  {!readOnly && !addingZoneToController && !editingZoneId && !bulkAddingToController && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => startAddZone(controller.id)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Plus size={16} />
                        {t('zones.addZone')}
                      </button>
                      <button
                        onClick={() => startBulkAddZones(controller.id)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Grid3x3 size={16} />
                        {t('zones.bulkAdd')}
                      </button>
                      {controller.zones.some((z) => selectedZones.has(z.id)) && (
                        <button
                          onClick={() => openBulkManageModal(controller.id)}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                        >
                          <Edit2 size={16} />
                          {t('zones.manageZones', { n: controller.zones.filter((z) => selectedZones.has(z.id)).length })}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {!readOnly && (addingZoneToController === controller.id || editingZoneId) && (
                  <div className="bg-subtle border border-line rounded-lg p-3 mb-3">
                    <h6 className="font-semibold text-theme-primary mb-3 text-sm">
                      {editingZoneId ? t('zones.editTitle') : t('zones.newTitle')}
                    </h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-theme-primary mb-1">
                          {t('zones.zoneNumberRequired')}
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={zoneFormData.zone_number}
                          onChange={(e) => setZoneFormData({ ...zoneFormData, zone_number: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-theme-primary mb-1">
                          {t('zones.type')}
                        </label>
                        <select
                          value={zoneFormData.type}
                          onChange={(e) => setZoneFormData({ ...zoneFormData, type: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="">{t('zones.selectType')}</option>
                          {zoneTypes.map((type) => (
                            <option key={type.id} value={type.name}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-theme-primary mb-1">
                          {t('zones.locationRequired')}
                        </label>
                        <input
                          type="text"
                          value={zoneFormData.location}
                          onChange={(e) => setZoneFormData({ ...zoneFormData, location: e.target.value })}
                          placeholder={t('zones.locationPlaceholder')}
                          className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <PhotoInput
                          label={t('zones.photoLabel')}
                          preview={zonePhotoPreview}
                          placeholderIcon={<Grid3x3 size={20} className="text-theme-secondary" />}
                          onFileChange={(file, preview) => {
                            setZoneFormData((fd) => ({ ...fd, photo: file }));
                            setZonePhotoPreview(preview);
                          }}
                          onDelete={editingZoneId
                            ? () => deleteZonePhoto(editingZoneId)
                            : () => { setZonePhotoPreview(null); setZoneFormData((fd) => ({ ...fd, photo: null })); }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-theme-primary mb-2">
                          {t('gps.location')}
                        </label>
                        {zoneFormData.gps_location ? (
                          <div className="space-y-1.5">
                            <div className="text-xs text-theme-secondary bg-subtle border border-line rounded-lg px-2 py-1.5 font-mono">
                              {zoneFormData.gps_location.latitude.toFixed(6)}, {zoneFormData.gps_location.longitude.toFixed(6)}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={captureZoneGPSLocation}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <MapPin size={13} />
                                {t('gps.update')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setZoneFormData((prev) => ({ ...prev, gps_location: null }))}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                              >
                                <X size={13} />
                                {t('gps.clear')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={captureZoneGPSLocation}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <MapPin size={13} />
                            {t('gps.setLocation')}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleSaveZone(controller.id)}
                        disabled={isSavingZone}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Save size={16} />
                        {tCommon('actions.save')}
                      </button>
                      <button
                        onClick={resetZoneForm}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                      >
                        <X size={16} />
                        {tCommon('actions.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {!readOnly && bulkAddingToController === controller.id && (
                  <div className="bg-subtle border border-line rounded-lg p-3 mb-3">
                    <h6 className="font-semibold text-theme-primary mb-3 text-sm">
                      {t('zones.bulkAddTitle')}
                    </h6>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-theme-primary mb-1">
                            {t('zones.startNumberRequired')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={bulkZoneFormData.start_number}
                            onChange={(e) => setBulkZoneFormData({ ...bulkZoneFormData, start_number: e.target.value })}
                            placeholder={t('zones.startNumberPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-theme-primary mb-1">
                            {t('zones.endNumberRequired')}
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={bulkZoneFormData.end_number}
                            onChange={(e) => setBulkZoneFormData({ ...bulkZoneFormData, end_number: e.target.value })}
                            placeholder={t('zones.endNumberPlaceholder')}
                            className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-theme-primary mb-1">
                          {t('zones.zoneType')}
                        </label>
                        <select
                          value={bulkZoneFormData.type}
                          onChange={(e) => setBulkZoneFormData({ ...bulkZoneFormData, type: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">{t('zones.selectType')}</option>
                          {zoneTypes.map((type) => (
                            <option key={type.id} value={type.name}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-theme-primary mb-1">
                          {t('zones.locationPattern')}
                        </label>
                        <input
                          type="text"
                          value={bulkZoneFormData.location_pattern}
                          onChange={(e) => setBulkZoneFormData({ ...bulkZoneFormData, location_pattern: e.target.value })}
                          placeholder={t('zones.locationPatternPlaceholder')}
                          className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-theme-primary mt-1">
                          {t('zones.locationPatternHint')}
                        </p>
                      </div>
                      {bulkZoneFormData.start_number && bulkZoneFormData.end_number
                       && parseInt(bulkZoneFormData.start_number) <= parseInt(bulkZoneFormData.end_number) && (
                        <div className="bg-surface border border-line rounded-lg p-2">
                          <p className="text-xs font-medium text-theme-primary mb-1">
                            {t('zones.bulkPreviewLabel', { count: parseInt(bulkZoneFormData.end_number) - parseInt(bulkZoneFormData.start_number) + 1 })}
                          </p>
                          <p className="text-xs text-theme-secondary">
                            {t('zones.bulkPreviewExample', {
                              n: bulkZoneFormData.start_number,
                              label: bulkZoneFormData.location_pattern
                                ? bulkZoneFormData.location_pattern.replace('{number}', bulkZoneFormData.start_number)
                                : `Zone ${bulkZoneFormData.start_number}`,
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleBulkAddZones(controller.id)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Save size={16} />
                        {t('zones.createZones')}
                      </button>
                      <button
                        onClick={resetBulkZoneForm}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
                      >
                        <X size={16} />
                        {tCommon('actions.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {!readOnly && controller.zones.length > 0 && (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={controller.zones.every((z) => selectedZones.has(z.id))}
                      onChange={() => toggleAllZonesInController(controller.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-theme-secondary">{t('zones.selectAll')}</span>
                  </div>
                )}

                <div className="space-y-2">
                  {controller.zones.length === 0 ? (
                    <div className="text-center py-6 text-theme-secondary text-sm">
                      {t('zones.empty')}. {t('zones.emptyHint')}
                    </div>
                  ) : (
                    controller.zones.map((zone) => (
                      <div
                        key={zone.id}
                        className="bg-surface border border-line rounded-lg p-3 flex items-center justify-between hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center gap-3">
                          {!readOnly && (
                            <input
                              type="checkbox"
                              checked={selectedZones.has(zone.id)}
                              onChange={() => toggleZoneSelection(zone.id)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                          {zone.photo_url ? (
                            <img
                              src={zone.photo_url}
                              alt={`Zone ${zone.zone_number}`}
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-subtle rounded-lg flex items-center justify-center">
                              <Grid3x3 size={20} className="text-theme-secondary" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-theme-primary text-sm">Zone {zone.zone_number}</span>
                              <span className="text-xs px-2 py-0.5 bg-subtle text-theme-secondary rounded">
                                {zone.type}
                              </span>
                              {zone.gps_location && (
                                <span className="flex items-center gap-0.5 text-xs text-green-600" title="GPS tagged">
                                  <MapPin size={11} />
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-theme-secondary">{zone.location}</p>
                          </div>
                        </div>
                        {!readOnly && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => startEditZone(zone)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                              title={t('zones.editTitle')}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteZoneConfirm({ id: zone.id, number: zone.zone_number })}
                              className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                              title={t('zones.deleteTitle')}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {controllers.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-subtle rounded-lg">
          <Monitor size={48} className="mx-auto text-theme-secondary mb-4" />
          <h4 className="text-lg font-semibold text-theme-secondary mb-2">{t('controllers.empty')}</h4>
          <p className="text-theme-secondary">{t('controllers.emptyHint')}</p>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteControllerConfirm !== null}
        title={t('controllers.deleteTitle')}
        message={t('controllers.deleteMessage', { desc: deleteControllerConfirm?.description ?? '' })}
        confirmText={tCommon('actions.delete')}
        cancelText={tCommon('actions.cancel')}
        variant="danger"
        onConfirm={handleDeleteController}
        onCancel={() => setDeleteControllerConfirm(null)}
      />

      <ConfirmModal
        isOpen={overrideViolations !== null}
        title={tCommon('waterRestrictions.overrideTitle', 'Override water restriction policy?')}
        message={tCommon('waterRestrictions.overridePrompt', {
          city: waterPolicy?.city ?? tCommon('waterRestrictions.theCity', 'the city'),
          defaultValue: `This schedule violates the ${waterPolicy?.city ?? 'city'} water restriction policy:`,
        })}
        confirmText={tCommon('waterRestrictions.overrideConfirm', 'Apply anyway (override)')}
        cancelText={tCommon('waterRestrictions.overrideCancel', 'Go back and edit')}
        variant="warning"
        isLoading={isSavingController}
        onConfirm={() => {
          const violations = overrideViolations ?? [];
          setOverrideViolations(null);
          const controllerNum = parseInt(formData.controller_number);
          void logEvent('water_restriction_overridden', `Override applied for controller #${controllerNum}`, {
            metadata: { job_site_id: jobSiteId, city: waterPolicy?.city, violations: violations.map(formatViolation) },
          });
          void persistController(controllerNum, {
            acknowledged: true,
            acknowledged_at: new Date().toISOString(),
            violations: violations.map(formatViolation),
          });
        }}
        onCancel={() => setOverrideViolations(null)}
      >
        <ul className="mt-2 list-disc pl-6 text-sm text-theme-primary">
          {(overrideViolations ?? []).map((v, i) => (
            <li key={i}>{formatViolation(v)}</li>
          ))}
        </ul>
      </ConfirmModal>

      <ConfirmModal
        isOpen={deleteZoneConfirm !== null}
        title={t('zones.deleteTitle')}
        message={t('zones.deleteMessage', { n: deleteZoneConfirm?.number ?? 0 })}
        confirmText={tCommon('actions.delete')}
        cancelText={tCommon('actions.cancel')}
        variant="danger"
        onConfirm={handleDeleteZone}
        onCancel={() => setDeleteZoneConfirm(null)}
      />

      <ConfirmModal
        isOpen={bulkAddResultModal !== null}
        title={bulkAddResultModal?.success ? t('zones.bulkResultSuccess') : t('zones.bulkResultError')}
        message={bulkAddResultModal?.message || ''}
        confirmText={tCommon('actions.ok')}
        cancelText={tCommon('actions.ok')}
        variant={bulkAddResultModal?.success ? 'success' : 'danger'}
        onConfirm={() => setBulkAddResultModal(null)}
        onCancel={() => setBulkAddResultModal(null)}
      />

      {bulkManageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-line">
              <h3 className="text-xl font-semibold text-theme-primary">{t('zones.manageSelectedTitle')}</h3>
              <p className="text-sm text-theme-secondary mt-1">
                {t('zones.zonesSelected', { count: bulkManageModal.zones.length })}
              </p>
            </div>

            <div className="p-6">
              <div className="bg-subtle rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-theme-primary mb-2 text-sm">{t('zones.selectedZonesLabel')}</h4>
                <div className="flex flex-wrap gap-2">
                  {bulkManageModal.zones.map((zone) => (
                    <span key={zone.id} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{/* theme-allow: badge chip keeps its accent tint */}
                      Zone {zone.zone_number}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-theme-primary text-sm">{t('zones.bulkUpdatePropsLabel')}</h4>
                <p className="text-xs text-theme-secondary">{t('zones.bulkUpdateHint')}</p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-theme-primary mb-1">
                      {t('zones.typeLabel')}
                    </label>
                    <select
                      value={bulkUpdateFormData.type}
                      onChange={(e) => setBulkUpdateFormData({ ...bulkUpdateFormData, type: e.target.value })}
                      className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">{t('zones.keepExistingTypes')}</option>
                      {zoneTypes.map((type) => (
                        <option key={type.id} value={type.name}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-primary mb-1">
                      {t('zones.locationLabel')}
                    </label>
                    <input
                      type="text"
                      value={bulkUpdateFormData.location}
                      onChange={(e) => setBulkUpdateFormData({ ...bulkUpdateFormData, location: e.target.value })}
                      placeholder={t('zones.keepExistingLocations')}
                      className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-line flex gap-3">
              <button
                onClick={handleBulkUpdateZones}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save size={18} />
                {t('zones.updateZones')}
              </button>
              <button
                onClick={handleBulkDeleteSelectedZonesRequest}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 size={18} />
                {t('zones.deleteZones')}
              </button>
              <button
                onClick={() => setBulkManageModal(null)}
                className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                {tCommon('actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={bulkDeleteConfirmModal !== null}
        title={t('zones.deleteSelectedTitle')}
        message={t('zones.deleteSelectedMessage', { count: bulkDeleteConfirmModal?.zones.length || 0 })}
        confirmText={t('zones.deleteZones')}
        cancelText={tCommon('actions.cancel')}
        variant="danger"
        isLoading={isDeletingZones}
        loadingText={t('zones.deletingZones')}
        onConfirm={handleBulkDeleteSelectedZones}
        onCancel={() => setBulkDeleteConfirmModal(null)}
      />

      <LocationPickerModal
        open={controllerGpsModalOpen}
        existing={formData.gps_location}
        fallback={propertyLocation}
        onSave={(loc) => setFormData((prev) => ({ ...prev, gps_location: loc }))}
        onClose={() => setControllerGpsModalOpen(false)}
      />
      <LocationPickerModal
        open={zoneGpsModalOpen}
        existing={zoneFormData.gps_location}
        fallback={propertyLocation}
        onSave={(loc) => setZoneFormData((prev) => ({ ...prev, gps_location: loc }))}
        onClose={() => setZoneGpsModalOpen(false)}
      />

      {NotifyModal}
    </div>
  );
}
